import { BaseApiClient } from './core';
import { logWarn } from '@/lib/logger';
import type { Organization, Repository, SyncProgress, TimePeriod } from '@/types/core';
import type { RepositoryContributors, RepositoryMetrics, TrendsData } from '@/types/developer';
import type { FeedbackCreateRequest, FeedbackCreateResponse } from '@/types/marketing';
import type { AccountContext, GitHubOrg, GitHubRepo, OnboardingFriction, OrgLookupResult, SuggestedReposResponse } from '@/types/notifications';

export interface RepoMetricValue {
  value: number;
  period_start: string;
  period_end: string;
}

export interface RepoWithMetrics {
  id: string;
  name: string;
  full_name: string;
  is_active: boolean;
  metrics: Record<string, RepoMetricValue>;
}

export class RepositoryApiClient extends BaseApiClient {
  // Repositories
  /**
   * Repo League: each active repo + its latest metric snapshots in one call.
   * Not gated by the individual-metrics opt-in (repos are not people).
   */
  async getRepositoriesBulkMetrics(
    orgId: string,
    timePeriod: TimePeriod = 'monthly',
    excludeBots = true
  ): Promise<{ repositories: RepoWithMetrics[]; total: number; has_more: boolean }> {
    const { data } = await this.client.get<{ repositories: RepoWithMetrics[]; total: number; has_more: boolean }>(
      '/repositories/bulk-metrics',
      { params: { orgId, timePeriod, excludeBots } }
    );
    return data;
  }
  async getRepositories(orgId: string, page = 1, pageSize = 50) {
    const { data } = await this.client.get<{ repositories: Repository[]; total: number; page: number; page_size: number }>('/repositories', {
      params: { orgId, page, pageSize },
    });
    return data;
  }
  /**
   * Fetch ALL repositories for an org, handling pagination automatically.
   * Use this for dropdowns/filters where the full list is needed.
   */
  async getAllRepositories(orgId: string): Promise<Repository[]> {
    const PAGE_SIZE = 100;
    const firstPage = await this.getRepositories(orgId, 1, PAGE_SIZE);
    const allRepos = [...firstPage.repositories];

    if (firstPage.total <= PAGE_SIZE) {
      return allRepos;
    }

    const totalPages = Math.ceil(firstPage.total / PAGE_SIZE);
    const remainingPages = Array.from({ length: totalPages - 1 }, (_, i) => i + 2);

    // allSettled (not all): one slow/failing page shouldn't discard every other
    // page's data. With Promise.all a single rejected request throws away the
    // whole list and, combined with a hung request, could stall the caller's
    // loading state. Collect whatever pages succeed; page 1 is always present.
    const results = await Promise.allSettled(
      remainingPages.map((page) => this.getRepositories(orgId, page, PAGE_SIZE))
    );

    const failedPages = results.filter((r) => r.status === 'rejected').length;
    if (failedPages > 0) {
      logWarn(
        `getAllRepositories: ${failedPages}/${remainingPages.length} repository pages failed to load; returning a partial list.`
      );
    }

    for (const result of results) {
      if (result.status === 'fulfilled') {
        allRepos.push(...result.value.repositories);
      }
    }

    return allRepos;
  }
  async getRepository(id: string): Promise<Repository> {
    const { data } = await this.client.get(`/repositories/${id}`);
    return data;
  }
  async createRepository(repo: Partial<Repository>): Promise<Repository> {
    const { data } = await this.client.post('/repositories', repo);
    return data;
  }
  async syncRepository(id: string, syncType: 'full' | 'incremental'): Promise<{ status: string; message: string }> {
    const { data } = await this.client.post(`/repositories/${id}/sync`, { sync_type: syncType });
    return data;
  }
  async getSyncStatus(repoId: string): Promise<SyncProgress> {
    const { data } = await this.client.get(`/repositories/${repoId}/sync-status`);
    return data;
  }
  async getBatchSyncStatus(repoIds: string[]): Promise<{ repositories: SyncProgress[] }> {
    const { data } = await this.client.get('/repositories/batch-sync-status', {
      params: { repoIds: repoIds.join(',') },
    });
    return data;
  }
  async submitFeedback(payload: FeedbackCreateRequest): Promise<FeedbackCreateResponse> {
    const { data } = await this.client.post('/feedback', payload);
    return data;
  }
  async deleteRepository(id: string): Promise<void> {
    await this.client.delete(`/repositories/${id}`);
  }
  /**
   * Delete several repositories at once (e.g. cleaning up lost-access repos).
   * Only repos belonging to `orgId` are removed; unknown/foreign ids are ignored
   * server-side. Returns how many were actually deleted.
   */
  async bulkDeleteRepositories(orgId: string, repositoryIds: string[]): Promise<{ deleted_count: number; deleted_ids: string[] }> {
    const { data } = await this.client.post<{ deleted_count: number; deleted_ids: string[] }>(
      '/repositories/bulk-delete',
      { org_id: orgId, repository_ids: repositoryIds }
    );
    return data;
  }
  async getRepositoryMetrics(id: string, timePeriod: TimePeriod = 'monthly'): Promise<RepositoryMetrics> {
    const { data } = await this.client.get(`/repositories/${id}/metrics`, {
      params: { timePeriod },
    });
    return data;
  }
  async getRepositoryContributors(id: string, limit = 10, excludeBots = true, days = 30): Promise<RepositoryContributors> {
    const { data } = await this.client.get(`/repositories/${id}/contributors`, {
      params: { limit, excludeBots, days },
    });
    return data;
  }
  async getRepositoryTrends(id: string, metricNames: string[], days = 30): Promise<TrendsData> {
    const { data } = await this.client.get(`/repositories/${id}/trends`, {
      params: {
        metricNames: metricNames.join(','),
        days,
      },
    });
    return data;
  }
  // GitHub Import (Onboarding)
  async getMyGitHubOrganizations(): Promise<GitHubOrg[]> {
    const { data } = await this.client.get('/github/my-organizations');
    return data;
  }
  async getGitHubRepositories(orgLogin: string, isPersonal: boolean = false): Promise<GitHubRepo[]> {
    const { data } = await this.client.get(`/github/organizations/${orgLogin}/repositories`, {
      params: { isPersonal },
    });
    return data;
  }
  async getSuggestedRepositories(
    orgLogin: string,
    isPersonal: boolean = false,
    suggestionLimit: number = 5
  ): Promise<SuggestedReposResponse> {
    const { data } = await this.client.get(`/github/organizations/${orgLogin}/suggested-repositories`, {
      params: { isPersonal, suggestionLimit },
    });
    return data;
  }
  async quickstartImport(orgLogin: string, isPersonal: boolean = false): Promise<Organization> {
    const { data } = await this.client.post(`/github/quickstart/${orgLogin}`, null, {
      params: { isPersonal },
    });
    return data;
  }
  async importDemoRepo(orgId: string, owner: string, repo: string): Promise<Repository> {
    const { data } = await this.client.post('/github/import-demo-repo', null, {
      params: { orgId, owner, repo },
    });
    return data;
  }
  // Token Management
  async validateOrganizationToken(orgId: string): Promise<{ status: string; message: string }> {
    const { data } = await this.client.post('/github/validate-token', null, {
      params: { orgId },
    });
    return data;
  }
  async refreshOrganizationToken(orgId: string): Promise<Organization> {
    const { data } = await this.client.post(`/github/organizations/${orgId}/refresh-token`);
    return data;
  }
  async getGitHubAuthUrlForOrg(orgId: string): Promise<{ authorization_url: string; state: string }> {
    const { data } = await this.client.get('/auth/github/authorize-for-org', {
      params: { orgId },
    });
    return data;
  }
  async getGitHubClientInfo(): Promise<{
    client_id?: string;
    settings_url: string;
    // Optional on purpose: the legacy (non-App-ready) backend branch omits this
    // key entirely, so it is `undefined` at runtime there. Consumers must treat a
    // missing value as "not enabled" (=== true / ?? false), never assume a boolean.
    github_app_enabled?: boolean;
    install_url?: string;
  }> {
    const { data } = await this.client.get('/auth/github/client-info');
    return data;
  }
  async getGitHubInstallUrl(): Promise<{ install_url: string; app_slug: string; state: string }> {
    const { data } = await this.client.get('/auth/github/install-url');
    return data;
  }
  async claimInstallation(installationId: number): Promise<Organization> {
    const { data } = await this.client.get('/auth/installation-callback', {
      params: { installation_id: installationId, setup_action: 'install' },
    });
    return data;
  }
  /**
   * Re-sync the caller's organization memberships from GitHub orgs and visible
   * App installations.
   *
   * GET /organizations is membership-scoped, and nothing created the membership for
   * a webhook-created shell org outside the login callback - so a user whose admin
   * approved the install mid-session stayed invisible to it until they logged out
   * and back in. Takes no arguments: it only ever reconciles the caller's own
   * memberships. Hidden-org App installations are resolved server-side and
   * still require an installation-token membership check. Never throws on a
   * GitHub failure; reports reconciled: false.
   */
  async reconcileOrganizations(): Promise<{ reconciled: boolean; organization_count: number }> {
    const { data } = await this.client.post<{ reconciled: boolean; organization_count: number }>(
      '/auth/organizations/reconcile'
    );
    return data;
  }
  /**
   * Record that a non-owner requested the GitHub App install (pending org-admin
   * approval), so we can follow up by email. Idempotent: one open request per user.
   * ``install_pending`` is kept separate from zero-org lead captures because only
   * a genuine install request may be auto-claimed after approval.
   */
  async recordInstallationRequest(orgLogin?: string | null, wallType?: string | null): Promise<{ status: string }> {
    const { data } = await this.client.post('/auth/installation-request', {
      org_login: orgLogin ?? null,
      wall_type: wallType ?? null,
    });
    return data;
  }
  /** Signals for the work-account recovery screen. Cached server-side for 10 min. */
  async getAccountContext(): Promise<AccountContext> {
    const { data } = await this.client.get<AccountContext>('/auth/account-context');
    return data;
  }
  /** Record that the user chose to continue without a work organization. */
  async acceptPersonalAccount(): Promise<{ accepted: boolean }> {
    const { data } = await this.client.post<{ accepted: boolean }>('/auth/account-context/accept-personal');
    return data;
  }
  /** "Leave your email, we'll reach out" fallback on the recovery escalation card. */
  async requestOnboardingCallback(): Promise<{ requested: boolean }> {
    const { data } = await this.client.post<{ requested: boolean }>('/auth/account-context/request-callback');
    return data;
  }
  /** Friction summary for the persistent org-connect banner. */
  async getOnboardingFriction(): Promise<OnboardingFriction> {
    const { data } = await this.client.get<OnboardingFriction>('/auth/onboarding-friction');
    return data;
  }
  /**
   * Resolve a user-declared org name via GitHub's PUBLIC /orgs/{login} endpoint -
   * works even when the org is invisible to our token or the membership is
   * private. 404-equivalent ("no such org") comes back as `found: false` with a
   * 200, not a rejected promise; a rejection here means the request itself
   * failed (network error, or a 422 for a login that fails GitHub's grammar).
   */
  async lookupOrg(login: string): Promise<OrgLookupResult> {
    const { data } = await this.client.get<OrgLookupResult>('/auth/github/org-lookup', { params: { login } });
    return data;
  }
  /** Email the user's org admin a one-click "approve & install" invite. */
  async inviteOrgAdmin(adminEmail: string, orgLogin?: string | null): Promise<{ sent: boolean }> {
    const { data } = await this.client.post<{ sent: boolean }>('/auth/installation-request/invite-admin', {
      admin_email: adminEmail,
      org_login: orgLogin ?? null,
    });
    return data;
  }
  // Pre-import workflow check
  async checkRepoWorkflow(owner: string, repo: string): Promise<{
    owner: string;
    repo: string;
    has_pr_workflow: boolean;
    recent_pr_count: number;
    total_merged_prs: number;
    commit_count: number;
    recommendation: string;
  }> {
    const { data } = await this.client.get(`/github/repos/${owner}/${repo}/workflow-check`);
    return data;
  }
}

export const repositoryApi = new RepositoryApiClient();
